import React from "react";
import { baseColor } from "../..";

export type BtnProps = {
  block?: boolean;
  children: React.ReactNode;
  className?: string;
  color?: "blue" | "green" | "red" | "yellow" | "indigo" | "dark" | "violet" | "gray" | "lightGray" | "orange" | "darkOrange" | "black";
  disabled?: boolean;
  outline?: boolean;
  rounded?: boolean;
  size?: "sm" | "md" | "lg";
  submit?: boolean;
  onClick?: (event: any) => void;
  // isLoading: boolean;
};

type ButtonRef = React.ForwardedRef<HTMLButtonElement>;

const style = {
  rounded: `rounded-full`,
  block: `flex justify-center w-full`,
  default: `flex px-4 m justify-center font-medium rounded-md focus:outline-none shadow transition ease-in duration-200`,
  disabled: `opacity-60 cursor-not-allowed`,
  outlineDisabled: `active:bg-inherit active:text-current`,
  sizes: {
    sm: "px-6 py-1 text-sm",
    md: "px-6 py-2",
    lg: "px-6 py-3 text-lg",
  },
  color: {
    primary: {
      bg: `text-white bg-blue-600 focus:ring-2 focus:ring-offset-2 hover:bg-blue-700 focus:ring-blue-500 focus:ring-offset-blue-200 `,
      outline: `border-blue-600 border-2 text-blue-700 active:bg-blue-600 active:text-white`,
      shadow: `shadow-lg shadow-blue-500/50`,
    },
    success: {
      bg: `text-white bg-green-600 focus:ring-2 focus:ring-offset-2 hover:bg-green-700 focus:ring-green-500 focus:ring-offset-green-200`,
      outline: `border-green-600 border-2 text-green-700 active:bg-green-600 active:text-white`,
      shadow: `shadow-lg shadow-green-500/50`,
    },
    danger: {
      bg: `text-white bg-red-600 focus:ring-2 focus:ring-offset-2 hover:bg-red-700 focus:ring-red-500 focus:ring-offset-red-200`,
      outline: `border-red-600 border-2 text-red-600 active:bg-red-600 active:text-white`,
      shadow: `shadow-lg shadow-red-500/50`,
    },
    dark: {
      bg: `text-white bg-black focus:ring-2 focus:ring-offset-2 hover:bg-black-600 focus:ring-gray-500 focus:ring-offset-gray-200`,
      outline: `border-black border-2 text-gray-900 active:bg-black active:text-white`,
      shadow: `shadow-lg shadow-red-500/50`,
    },
    warning: {
      bg: `text-white bg-yellow-500 focus:ring-2 focus:ring-offset-2 hover:bg-yellow-600 focus:ring-yellow-500 focus:ring-offset-yellow-200`,
      outline: `border-yellow-500 border-2 text-yellow-500 active:bg-yellow-500 active:text-white`,
      shadow: `shadow-lg shadow-yellow-500/50`,
    },
    indigo: {
      bg: `text-white bg-indigo-600 focus:ring-2 focus:ring-offset-2 hover:bg-indigo-700 focus:ring-indigo-600 focus:ring-offset-indigo-200`,
      outline: `border-indigo-600 border-2 text-indigo-600 active:bg-indigo-500 active:text-white`,
      shadow: `shadow-lg shadow-indigo-500/50`,
    },
    violet: {
      bg: `text-white bg-violet-600 focus:ring-2 focus:ring-offset-2 hover:bg-violet-700 focus:ring-violet-600 focus:ring-offset-violet-200`,
      outline: `border-violet-600 border-2 text-violet-600 active:bg-violet-500 active:text-white`,
      shadow: `shadow-lg shadow-violet-500/50`,
    },
    gray: {
      bg: `text-white bg-gray-500 focus:ring-2 focus:ring-offset-2 hover:bg-gray-700 focus:ring-gray-500 focus:ring-offset-gray-200`,
      outline: `border-gray-500 border-2 text-gray-500 active:bg-gray-500 active:text-white`,
      shadow: `shadow-lg shadow-gray-500/50`,
    },
    lightGray: {
      bg: `text-black bg-gray-200 focus:ring-2 focus:ring-offset-2 hover:bg-gray-700 focus:ring-gray-500 focus:ring-offset-gray-200`,
      outline: `border-gray-500 border-2 text-gray-500 active:bg-gray-500 active:text-white`,
      shadow: `shadow-lg shadow-gray-500/50`,
    },
    orange: {
      bg: `text-white bg-[#FF4B00] focus:ring-2 focus:ring-offset-2 hover:bg-[#CC3D00] focus:ring-[#FF4B00] focus:ring-offset-[#FF4B00]`,
      outline: `border-[#FF4B00] border-2 text-[#FF4B00] active:bg-[#FF4B00] active:text-white`,
      shadow: `shadow-lg shadow-[#FF4B00]/[#FFB899]`,
    },
    darkOrange: {
      bg: `text-white bg-[#EA6F31] focus:ring-2 focus:ring-offset-2 hover:bg-[#DB5B1B] focus:ring-[#EA5404] focus:ring-offset-[#FFEFE1]`,
      outline: `border-[#EA6F31] border-2 text-[#EA6F31] active:bg-[#EA6F31] active:text-white`,
      shadow: `shadow-lg shadow-[#EA6F31]/[#FFEFE1]`,
    },
    black: {
      bg: `text-gray-900 bg-gray-400 focus:ring-2 focus:ring-offset-2 hover:bg-gray-700 focus:ring-gray-500 focus:ring-offset-gray-200`,
    },
  },
};

const colors = (outline: boolean | undefined) => ({
  blue: outline ? style.color.primary.outline : style.color.primary.bg,
  green: outline ? style.color.success.outline : style.color.success.bg,
  red: outline ? style.color.danger.outline : style.color.danger.bg,
  dark: outline ? style.color.dark.outline : style.color.dark.bg,
  yellow: outline ? style.color.warning.outline : style.color.warning.bg,
  indigo: outline ? style.color.indigo.outline : style.color.indigo.bg,
  violet: outline ? style.color.violet.outline : style.color.violet.bg,
  gray: outline ? style.color.gray.outline : style.color.gray.bg,
  lightGray: outline ? style.color.lightGray.outline : style.color.lightGray.bg,
  orange: outline ? style.color.orange.outline : style.color.orange.bg,
  darkOrange: outline ? style.color.darkOrange.outline : style.color.darkOrange.bg,
  black: outline ? style.color.gray.outline : style.color.black.bg,
});

const Button = React.forwardRef(
  (
    {
      block = false,
      children,
      className,
      color = baseColor,
      disabled = false,
      outline,
      rounded,
      size = "md",
      submit,
      onClick,
      // isLoading = false,
      ...props
    }: BtnProps,
    ref: ButtonRef
  ) => (
    <button
      ref={ref}
      {...props}
      type={submit ? "submit" : "button"}
      disabled={disabled}
      onClick={onClick}
      className={`
        ${className}
        ${block ? style.block : ""}
        ${style.default}
        ${rounded ? style.rounded : "rounded"}
        ${color ? colors(outline)[color] : colors(outline).dark}
        ${disabled ? style.disabled : ""}
        ${outline && disabled && style.outlineDisabled}
        ${style.sizes[size]}
      `}
    >
      {/* <svg
        className="border-4 animate-spin h-5 w-5 mt-[1px] mr-3 rounded-full border-t-transparent"
        viewBox="0 0 20 2"
      ></svg> */}
      {children}
    </button>
  )
);

Button.displayName = "Button";

export default Button;
